<template>
    <div class="container-fluid">
        <PageBanner
            title="Data"
            subtext="Explore demographic, activity, flow, impact, table, and group reporting across Purapura Whetu data."
        />

        <q-tabs v-model="tab" class="tabs-container" active-color="primary">
            <q-tab no-caps class="client-graph-tab" name="demographic" label="Demographics" />
            <q-tab no-caps class="client-graph-tab" name="activity" label="Activity" />
            <q-tab no-caps class="client-graph-tab" name="clientFlow" label="Flow" />
            <q-tab no-caps class="client-graph-tab" name="clientImpacts" label="Impacts" />
            <q-tab no-caps class="client-graph-tab" name="clientTables" label="Tables" />
            <q-tab no-caps class="client-graph-tab" name="clientGroups" label="Groups" />
            <q-tab v-if="hasFeature('sroi')" no-caps class="client-graph-tab" name="sroi" label="SROI" />
        </q-tabs>

        <div class="filters-section" v-if="!(uniqueFilterTabs.includes(tab))">
            <h3 class="filters-title">
                <span>
                    <q-icon name="tune" class="filter-icon" />
                    Filters
                </span>
                <q-btn @click="showFilters = !showFilters" flat color="grey-7" icon="filter_list" label="Toggle Filters"/>
            </h3>

            <q-expansion-item v-if="showFilters" class="expansion-panel" :class="{ 'expanded': dataFiltersExpanded }"
                v-model="dataFiltersExpanded" label="Data Filters" switch-toggle-side expand-icon="keyboard_arrow_down"
                expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <!-- Database Filter -->
                        <div class="filter-input-group" v-if="databases.length > 1">
                            <label class="form-label">Database</label>
                            <q-select class="form-control-quasar" clearable outlined multiple
                                v-model="selectedDatabases" use-chips :options="databases" dense
                                bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of current databases from paua
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Programme</label>
                            <q-select class="form-control-quasar" clearable outlined multiple
                                v-model="selectedProgrammes" use-chips :options="sortedProgrammeList" dense
                                bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of current programmes within Paua
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Service</label>
                            <q-select class="form-control-quasar" clearable outlined multiple v-model="selectedServices"
                                use-chips :options="this.serviceList"
                                :disable="selectedDatabases && !selectedDatabases.includes('Purapura Whetu')" dense
                                bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of current services within Paua
                            </q-tooltip>
                        </div>
                    </div>
                </div>
            </q-expansion-item>

            <q-expansion-item v-if="showFilters" class="expansion-panel" :class="{ 'expanded': demographicsExpanded }"
                v-model="demographicsExpanded" label="Demographics Filters" switch-toggle-side
                expand-icon="keyboard_arrow_down" expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Age Range</label>
                            <q-select class="form-control-quasar" clearable outlined multiple v-model="selectedAges"
                                use-chips :options="this.ageList" dense bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of different age ranges
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Gender</label>
                            <q-select class="form-control-quasar" clearable outlined multiple v-model="selectedGenders"
                                use-chips :options="this.genderList" dense bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of genders
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Ethnicity</label>
                            <q-select class="form-control-quasar" clearable outlined multiple
                                v-model="selectedEthnicities" use-chips :options="this.ethnicityList" dense
                                bg-color="white" />
                            <q-tooltip class="text-body2">
                                This will display a list of different ethnicities
                            </q-tooltip>
                        </div>
                    </div>

                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Birth Date From</label>
                            <q-input class="form-control-quasar" outlined v-model="birthDateFrom" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="birthDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Birth Date To</label>
                            <q-input class="form-control-quasar" outlined v-model="birthDateTo" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="birthDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                    </div>
                </div>
            </q-expansion-item>

            <q-expansion-item v-if="showFilters" class="expansion-panel" :class="{ 'expanded': dateRangesExpanded }"
                v-model="dateRangesExpanded" label="Date Ranges" switch-toggle-side expand-icon="keyboard_arrow_down"
                expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Entry Date From</label>
                            <q-input class="form-control-quasar" outlined v-model="entryDateFrom" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="entryDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Entry Date To</label>
                            <q-input class="form-control-quasar" outlined v-model="entryDateTo" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="entryDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                    </div>

                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Exit Date From</label>
                            <q-input class="form-control-quasar" outlined v-model="exitDateFrom" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="exitDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Exit Date To</label>
                            <q-input class="form-control-quasar" outlined v-model="exitDateTo" mask="####-##-##"
                                :placeholder="dateFormat" clearable dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="exitDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                    </div>
                </div>
            </q-expansion-item>

            <q-expansion-item v-if="showFilters && tab === 'clientImpacts'" class="expansion-panel"
                :class="{ 'expanded': dateFiltersExpanded }" v-model="dateFiltersExpanded" label="Date Filters"
                switch-toggle-side expand-icon="keyboard_arrow_down" expanded-icon="keyboard_arrow_up"
                header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Filter Date From</label>
                            <q-input class="form-control-quasar" outlined v-model="filterDateFrom" mask="####-##-##"
                                :placeholder="dateFormat" clearable required dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="filterDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                If no filter from date is provided, will default to 90 days from current date.
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Filter Date To</label>
                            <q-input class="form-control-quasar" outlined v-model="filterDateTo" mask="####-##-##"
                                :placeholder="dateFormat" clearable required dense bg-color="white"
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="filterDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                If no filter to date is provided, will default to the current date.
                            </q-tooltip>
                        </div>
                    </div>
                </div>
            </q-expansion-item>

            <div v-if="showFilters" class="row justify-center">
                <q-option-group
                    v-model="selectedClientOptions"
                    :options="this.clientOptionsList"
                    color="primary"
                    type="checkbox"
                    inline
                />
            </div>

            <div v-if="showFilters" class="filter-actions">
                <q-btn class="btn btn-secondary" @click="resetFilters" outline color="grey-7" icon="refresh"
                    label="Reset" no-caps />
                <q-btn class="btn btn-primary" @click="applyFilters" color="secondary" icon="check" label="Apply Filters"
                    no-caps />
                    <q-icon v-show="formattedFilters != ''" class="q-ma-none" size="lg" color="secondary" name="o_filter_list"><q-tooltip class="formatted-filters text-body2">{{ formattedFilters }}</q-tooltip></q-icon>
            </div>

            <div v-if="!(uniqueFilterTabs.includes(tab)) && clientCount !== null" class="client-count-label">
                Viewing data from {{ clientCount }} {{ clientCount === 1 ? 'client' : 'clients' }}
            </div>
        </div>

        <q-tab-panels
            style="border-radius: 12px; background-color: transparent; flex: 1 1 auto; min-height: 0;"
            v-model="tab"
            class="d-flex flex-column"
        >
            <q-tab-panel name="demographic">
                <ClientDemographicComponent @displayErrors="displayErrors" :key="query" :query="query" />
            </q-tab-panel>

            <q-tab-panel name="activity">
                <ClientActivityComponent @displayErrors="displayErrors" :key="query" :query="query" />
            </q-tab-panel>

            <q-tab-panel name="clientFlow">
                <ClientFlowComponent @displayErrors="displayErrors" :key="query" :query="query" />
            </q-tab-panel>

            <q-tab-panel name="clientImpacts">
                <ClientImpactComponent :key="query" :query="query" />
            </q-tab-panel>

            <q-tab-panel name="clientTables">
                <ClientTableParentComponent :key="query" :query="query" />
            </q-tab-panel>

            <q-tab-panel name="clientGroups" style="margin: -1rem;">
                <ClientGroupsParentComponent :databaseList="databaseList" />
            </q-tab-panel>

            <q-tab-panel name="sroi" style="margin: -1rem;">
                <SroiComponent />
            </q-tab-panel>
        </q-tab-panels>
    </div>
</template>

<script>

import ClientDemographicComponent from "./ClientDemographicComponent.vue";
import ClientActivityComponent from "./ClientActivityComponent.vue";
import ClientFlowComponent from "./ClientFlowComponent.vue";
import ClientImpactComponent from "./ClientImpactComponent.vue";
import ClientTableParentComponent from "./ClientTableParentComponent.vue";
import ClientGroupsParentComponent from "./ClientGroupParentComponent.vue";
import SroiComponent from "../DataComponents/SroiComponent.vue";

import api from "../../services/api/api"
import notify from '@/services/util/notify';
import dayjs from "@/services/util/dayjs";
import token from "@/services/util/roleHandler";
import { hasFeature } from "@/services/util/featureHandler";
import PageBanner from "./DashboardComponents/PageBanner.vue";


import { ref } from 'vue'

const ClientDataGraphComponent = {
    name: "ClientDataGraph",
    components: { ClientDemographicComponent, ClientActivityComponent, ClientFlowComponent, ClientImpactComponent, ClientTableParentComponent, ClientGroupsParentComponent, SroiComponent, PageBanner },

    setup() {
        return {
            tab: ref('demographic'),
            splitterModel: ref(20)
        }
    },

    data() {
        return {
            user: token.getTokenFields(),
            hasFeature,

            showFilters: true,

            dayjs: dayjs,
            dateFormat: 'YYYY-MM-DD',

            filterDateFrom: "",
            filterDateTo: "",
            databaseList: {},
            programmeRawData: [],
            programmeList: [],
            displayedProgrammeList: [],
            serviceList: [],
            ethnicityList: [],
            ageList: ["0 - 11", "12 - 17", "18 - 24", "25 - 64", "65 +"],
            genderList: ['Male', 'Female', 'Gender Diverse'],
            clientOptionsList: [{
                label: 'Waitlist',
                value: 'Waitlist'
            },
            {
                label: 'Active',
                value: 'Active'
            },
            {
                label: 'Exited',
                value: 'Exited'
            }],

            selectedDatabases: null,
            selectedProgrammes: null,
            selectedServices: null,
            selectedEthnicities: null,
            selectedAges: null,
            selectedGenders: null,
            selectedClientOptions: ['Active'],

            birthDateFrom: '',
            birthDateTo: '',
            entryDateFrom: "",
            entryDateTo: "",
            exitDateFrom: "",
            exitDateTo: "",

            query: '',

            formattedFilters: "",

            clientCount: null,

            uniqueFilterTabs: ['clientGroups', 'sroi'],

            dataFiltersExpanded: false,
            demographicsExpanded: false,
            dateRangesExpanded: false,
            dateFiltersExpanded: false,
        }
    },

    created() {
        this.onIntialize()
    },

    computed: {
        // Ensure that the displayed programme list is always sorted.
        sortedProgrammeList() {
            return this.displayedProgrammeList.slice().sort()
        },

        // Populate list of databases from user's sources (or all for admin)
        databases() {
            if (this.user.role === 'admin' || this.user.role === 'developer') {
                return Object.keys(this.databaseList);
            } else {
                const sources = this.user.sources || ['purapura_whetu'];
                return sources.map(source => {
                    const capitalised = source.split("_").map((word) => {
                        return word[0].toUpperCase() + word.substring(1);
                    }).join(" ")
                    return capitalised;
                });
            }
        }
    },


    watch: {
        /**
         * @desc When a database is selected, update the list of available programmes to select from.
         */
        selectedDatabases() {
            if (this.selectedDatabases && this.selectedDatabases.length > 0) {
                this.displayedProgrammeList = []
                for (const database of this.selectedDatabases) {
                    const key = this.databaseList[database]
                    this.displayedProgrammeList = this.displayedProgrammeList.concat(this.programmeRawData[0][key])
                }
            } else {
                this.displayedProgrammeList = this.programmeList;
            }
        },


        tab(newVal) {
            this.resetFilterDates(newVal);
            this.applyFilters()
        } 
    },

    methods: {

        /**
         * @desc Set all the required chart data and chart options.
         */
        capitalise(s) {
            return s[0].toUpperCase() + s.slice(1);
        },

        onIntialize() {
            this.applyFilters();
            // Fetch all client data from backend. Returns list of data once all requests have completed

            const fetchData = () => Promise.all([
                    api.client.getServicesList(),
                    api.client.getEthnicityList(),
                    api.client.getProgrammeList(),
                    api.client.getDatabaseList(),
                ])

            fetchData().then((values) => {
                this.serviceList = values[0]['data']['services']
                this.ethnicityList = values[1]['data']['ethnicities']
                this.programmeRawData = values[2]['data']['programmes']

                // Loop through the values of the object
                for (const values of Object.values(this.programmeRawData[0])) {
                    // Concatenate the current values array to the concatenated array
                    this.programmeList.push(...values);
                }

                values[3].data.databases.forEach(source => {
                    const capitalised = source.split("_").map((word) => {
                        return word[0].toUpperCase() + word.substring(1);
                    }).join(" ")
                    this.databaseList[capitalised] = source;
                });

                this.displayedProgrammeList = this.programmeList;
            }).catch((error) => {
                console.error(error.message);
                notify.error("Error retrieving filter lists", "top");
            })

        },


        /**
         * @desc Apply the team to all the graph (perform get requests with the team name in the query)
         */
        applyFilters() {
            const filters = {}

            this.checkFilters()

            if (this.selectedAges) { filters.age_range_id = this.selectedAges }
            if (this.selectedGenders) { filters.gender_id = this.selectedGenders }
            if (this.selectedProgrammes) { filters.programme_id = this.selectedProgrammes }
            if (this.selectedServices) { filters.service_type_id = this.selectedServices }
            if (this.selectedDatabases) {
                filters.source = this.selectedDatabases.map((database) => {
                    return this.databaseList[database]
                });
            }
            if (this.selectedEthnicities) { filters.ethnicity_id = this.selectedEthnicities }
            if (this.birthDateFrom) {filters.birthDateFrom = this.birthDateFrom}
            if (this.birthDateTo) {filters.birthDateTo = this.birthDateTo}
            if (this.filterDateFrom) {filters.dateFrom = this.filterDateFrom}
            if (this.filterDateTo) {filters.dateTo = this.filterDateTo}
            if (this.entryDateFrom) {filters.entryDateFrom = this.entryDateFrom}
            if (this.entryDateTo) {filters.entryDateTo = this.entryDateTo}
            if (this.exitDateFrom) {filters.exitDateFrom = this.exitDateFrom}
            if (this.exitDateTo) {filters.exitDateTo = this.exitDateTo}
            if (this.selectedClientOptions) {filters.status_id = this.selectedClientOptions}

            this.formatFilters(filters);
            this.query = `?filters=${encodeURIComponent(JSON.stringify(filters))}`

            api.clientGraph.clientCount(this.query).then((res) => {
                this.clientCount = res.data.count;
            }).catch(() => {
                this.clientCount = null;
            });
        },


        /**
         * @desc Formats the filters to display in the tooltip
         * @param {*} filterObject 
         */
        formatFilters(filterObject) {
            this.formattedFilters = [];

            for (let [key, values] of Object.entries(filterObject)) {
                key = key.charAt(0).toUpperCase() + key.slice(1);
                if (Array.isArray(values)) {
                    key = key.split('_')[0]
                    const formattedValues = values.join(', ');
                    this.formattedFilters.push(`${key}: ${formattedValues}`);
                } else {
                    if (this.activeStaff) { values = 'true'}
                    this.formattedFilters.push(`${key}: ${values}`);
                }
            }

            this.formattedFilters = this.formattedFilters.join('\n');
        },

        /**
        * @desc Reset all the filters and retrieve the chart data from the backend
        */
        resetFilters() {
            this.selectedAges = null;
            this.selectedGenders = null;
            this.selectedServices = null;
            this.selectedEthnicities = null;
            this.selectedProgrammes = null;
            this.selectedDatabases = null;
            this.birthDateFrom =  '';
            this.birthDateTo =  '';
            this.entryDateFrom =  '';
            this.entryDateTo =  '';
            this.exitDateFrom =  '';
            this.exitDateTo =  '';
            this.query = '';
            this.formattedFilters = '';
            this.selectedClientOptions = ['Active'];
            this.resetFilterDates(this.tab);
        },


        resetFilterDates(newVal) {
            if (newVal == "clientImpacts") {
                this.filterDateFrom = dayjs().subtract(90, 'days').format('YYYY-MM-DD');
                this.filterDateTo = dayjs().format('YYYY-MM-DD');
            } else {
                this.filterDateFrom = "";
                this.filterDateTo = "";
            }
        },


        /**
         * @desc Because we use chips, if the user selects a chip and clears it, the v-model is set to an empty array.
         * We need to go through all the filters, and if they are an empty array simply set it to null
         */
        checkFilters() {
            this.selectedAges = (this.selectedAges !== null && this.selectedAges.length > 0) ? this.selectedAges : null;
            this.selectedGenders = (this.selectedGenders !== null && this.selectedGenders.length > 0) ? this.selectedGenders : null;
            this.selectedProgrammes = (this.selectedProgrammes !== null && this.selectedProgrammes.length > 0) ? this.selectedProgrammes : null;
            this.selectedServices = (this.selectedServices !== null && this.selectedServices.length > 0) ? this.selectedServices : null;
            this.selectedDatabases = (this.selectedDatabases !== null && this.selectedDatabases.length > 0) ? this.selectedDatabases : null;
            this.selectedEthnicities = (this.selectedEthnicities !== null && this.selectedEthnicities.length > 0) ? this.selectedEthnicities : null;
            this.selectedClientOptions = (this.selectedClientOptions !== null && this.selectedClientOptions.length > 0) ? this.selectedClientOptions : ['Active'];
        },


        /**
         * @desc If there is an error retrieving any of the graphs, display that error but still show the other graphs.
         * @param {*} values 
         */
        displayErrors(values) {
            const errors = values.filter(value => value.error);
            if (errors.length > 0) {
                // Display specific error messages
                errors.forEach(error => {
                    if (error.error.response && error.error.response.data) {
                        notify.error(error.error.response.data, 'top');
                    } else {
                        notify.error("Error retrieving clientGraph data", 'top');
                    }
                });
            }
        }
    }
}

export default ClientDataGraphComponent;
</script>

<style scoped>
@import '../../assets/styles/clientDemographics.css';

.client-count-label {
    text-align: center;
    font-size: 0.875rem;
    color: #6c757d;
    padding: 0.5rem 0 0.25rem;
}
</style>
